import axios from "axios";

// The stable codes a refused proposal save carries. Only the ones the editor
// words for itself are listed; every other code keeps the generic message.
export enum ProposalSaveErrorCode {
  // The deal has no contract, so there is nothing to attach the proposal to.
  NO_LIVE_CONTRACT = "NO_LIVE_CONTRACT",
  DEAL_CLOSED = "DEAL_CLOSED",
  // Someone saved the proposal after this edit began.
  STALE_DRAFT = "STALE_DRAFT",
  // The proposal was sent; only a draft is saved.
  NOT_A_DRAFT = "NOT_A_DRAFT",
  // A newer template was loaded after this proposal was generated.
  TEMPLATE_CHANGED = "TEMPLATE_CHANGED",
  // A Send started: the draft stays as it went out.
  SEND_IN_PROGRESS = "SEND_IN_PROGRESS",
  // A typed field is longer than the template allows.
  VALUE_TOO_LONG = "VALUE_TOO_LONG",
  // More recipients sign than the template has signature places.
  TOO_MANY_SIGNERS = "TOO_MANY_SIGNERS"
}

const KEY_PREFIX = "contracts.proposals.editor";

const MESSAGE_KEY_BY_CODE: Record<ProposalSaveErrorCode, string> = {
  [ProposalSaveErrorCode.NO_LIVE_CONTRACT]: `${KEY_PREFIX}.saveErrorNoContract`,
  [ProposalSaveErrorCode.DEAL_CLOSED]: `${KEY_PREFIX}.saveErrorDealClosed`,
  [ProposalSaveErrorCode.STALE_DRAFT]: `${KEY_PREFIX}.saveErrorStale`,
  [ProposalSaveErrorCode.NOT_A_DRAFT]: `${KEY_PREFIX}.saveErrorNotADraft`,
  [ProposalSaveErrorCode.TEMPLATE_CHANGED]: `${KEY_PREFIX}.saveErrorTemplateChanged`,
  [ProposalSaveErrorCode.SEND_IN_PROGRESS]: `${KEY_PREFIX}.saveErrorSendInProgress`,
  [ProposalSaveErrorCode.VALUE_TOO_LONG]: `${KEY_PREFIX}.saveErrorValueTooLong`,
  [ProposalSaveErrorCode.TOO_MANY_SIGNERS]: `${KEY_PREFIX}.saveErrorTooManySigners`
};

const GENERIC_KEY = `${KEY_PREFIX}.saveError`;

// Own-property only: `in` would accept inherited names like "toString".
const isKnownCode = (code: unknown): code is ProposalSaveErrorCode =>
  typeof code === "string" &&
  Object.prototype.hasOwnProperty.call(MESSAGE_KEY_BY_CODE, code);

// The i18n key for a refused save, read from the refusal's `code`. The
// server's own `message` is not shown: it is written for a log, not a rep, and
// it is not translated.
const refusalCode = (error: unknown): unknown =>
  axios.isAxiosError(error)
    ? (error.response?.data as { code?: unknown } | undefined)?.code
    : undefined;

export const saveErrorMessageKey = (error: unknown): string => {
  const code = refusalCode(error);
  return isKnownCode(code) ? MESSAGE_KEY_BY_CODE[code] : GENERIC_KEY;
};

// The save was refused because a Send for the draft has started.
export const isRefusedWhileSending = (error: unknown) =>
  refusalCode(error) === ProposalSaveErrorCode.SEND_IN_PROGRESS;
